import { create } from 'zustand';
import { api } from '@/lib/api/client';
import { offlineTaskQueue } from '@/lib/offlineQueue';
import type { Task, TasksResponse, TaskStatus, TaskStats } from '@/lib/api/types';

interface TaskState {
  tasks: Task[];
  selectedTask: Task | null;
  stats: TaskStats;
  total: number;
  isLoading: boolean;
  error: string | null;
  filter: TaskStatus | 'ALL';

  // Actions
  fetchTasks: (status?: TaskStatus) => Promise<void>;
  fetchTaskById: (id: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  updateTaskStatus: (id: string, status: TaskStatus) => Promise<void>;
  setFilter: (filter: TaskStatus | 'ALL') => void;
  clearError: () => void;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  selectedTask: null,
  stats: { new: 0, inProgress: 0, completedToday: 0 },
  total: 0,
  isLoading: false,
  error: null,
  filter: 'ALL',

  fetchTasks: async (status?: TaskStatus) => {
    set({ isLoading: true, error: null });
    try {
      const params = status ? { status } : undefined;
      const response = await api.get<TasksResponse>('/tasks', params);
      set({
        tasks: response.items,
        total: response.total,
        isLoading: false,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch tasks',
        isLoading: false,
      });
    }
  },

  fetchTaskById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const task = await api.get<Task>(`/tasks/${id}`);
      set({ selectedTask: task, isLoading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch task',
        isLoading: false,
      });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.get<TaskStats>('/tasks/stats');
      set({ stats });
    } catch (err) {
      console.error('Failed to fetch task stats:', err);
    }
  },

  updateTaskStatus: async (id: string, status: TaskStatus) => {
    set({ isLoading: true, error: null });

    // Optimistically update the local state
    const now = new Date().toISOString();
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              status,
              startedAt: status === 'IN_PROGRESS' ? (t.startedAt || now) : t.startedAt,
              completedAt: status === 'COMPLETED' ? now : undefined,
              updatedAt: now,
            }
          : t
      ),
      selectedTask:
        state.selectedTask?.id === id
          ? {
              ...state.selectedTask,
              status,
              startedAt: status === 'IN_PROGRESS' ? (state.selectedTask.startedAt || now) : state.selectedTask.startedAt,
              completedAt: status === 'COMPLETED' ? now : undefined,
              updatedAt: now,
            }
          : state.selectedTask,
    }));

    // Check if we're offline
    if (!navigator.onLine) {
      try {
        await offlineTaskQueue.add(id, status);
        set({ isLoading: false });
        return;
      } catch (err) {
        set({
          error: 'Failed to queue update for later sync',
          isLoading: false,
        });
        throw err;
      }
    }

    try {
      const updatedTask = await api.patch<Task>(`/tasks/${id}/status`, { status });
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === id ? updatedTask : t)),
        selectedTask: state.selectedTask?.id === id ? updatedTask : state.selectedTask,
        isLoading: false,
      }));
      // Refresh stats after status update
      get().fetchStats();
    } catch (err) {
      // If network error, queue for later
      if (err instanceof Error && (err.message.includes('Network') || err.message.includes('fetch'))) {
        try {
          await offlineTaskQueue.add(id, status);
          set({ isLoading: false });
          return;
        } catch {
          // Fall through to error state
        }
      }
      // Revert optimistic update on error
      get().fetchTasks();
      set({
        error: err instanceof Error ? err.message : 'Failed to update task status',
        isLoading: false,
      });
      throw err;
    }
  },

  setFilter: (filter: TaskStatus | 'ALL') => {
    set({ filter });
    const { fetchTasks } = get();
    if (filter === 'ALL') {
      fetchTasks();
    } else {
      fetchTasks(filter);
    }
  },

  clearError: () => set({ error: null }),
}));
